interface NDEFRecord {
    recordType: string;
    mediaType?: string;
    id?: string;
    data?: DataView;
    encoding?: string;
    lang?: string;
}

interface NDEFMessage {
    records: NDEFRecord[];
}

type ReadingEventListener = (event: { message: NDEFMessage; serialNumber: string }) => void;
type ReadingErrorEventListener = () => void;

/**
 * MockNDEFReader - A mock implementation of the NDEFReader API for development testing
 *
 * This class simulates NFC tag reading functionality without requiring physical NFC hardware.
 * When scan() is called, it displays a button in the bottom-right corner that developers can
 * click to simulate tag reads with configurable tag UIDs.
 */
export class MockNDEFReader {
    /**
     * Static configuration property for setting a custom tag UID
     */
    public static mockTagUID: string = "14:5B:82:32:EC:4C:81";

    private readingListeners: ReadingEventListener[] = [];
    private readingErrorListeners: ReadingErrorEventListener[] = [];
    private mockButton: HTMLButtonElement | null = null;
    private mockContainer: HTMLDivElement | null = null;
    private mockInput: HTMLInputElement | null = null;

    /**
     * Implements the scan() method from the NDEFReader interface
     * Returns a resolved Promise and creates the mock button UI
     */
    public scan(): Promise<void> {
        this.createMockButton();
        return Promise.resolve();
    }

    /**
     * Add event listener for reading or readingerror events
     */
    public addEventListener(type: 'reading' | 'readingerror', listener: ReadingEventListener | ReadingErrorEventListener): void {
        if (type === 'reading') {
            this.readingListeners.push(listener as ReadingEventListener);
        } else if (type === 'readingerror') {
            this.readingErrorListeners.push(listener as ReadingErrorEventListener);
        }
    }

    /**
     * Remove event listener for reading or readingerror events
     */
    public removeEventListener(type: 'reading' | 'readingerror', listener: ReadingEventListener | ReadingErrorEventListener): void {
        if (type === 'reading') {
            this.readingListeners = this.readingListeners.filter(l => l !== listener);
        } else if (type === 'readingerror') {
            this.readingErrorListeners = this.readingErrorListeners.filter(l => l !== listener);
        }
    }

    /**
     * Create and inject the mock button into the DOM
     */
    private createMockButton(): void {
        if (this.mockContainer) {
            return; // Button already exists
        }

        // Create container (wrapper for absolute positioning)
        this.mockContainer = document.createElement('div');
        Object.assign(this.mockContainer.style, {
            position: 'fixed',
            padding: '15px 15px',
            top: '20px',
            right: '20px',
            zIndex: '9999',
            overflow: 'hidden',
            height: '75px',
            width: '620px'
        });

        // Create input box container
        const inputBox = document.createElement('div');
        Object.assign(inputBox.style, {
            position: 'absolute',
            right: '-85px',
            top: '10px',
            opacity: '0',
            display: 'flex',
            alignItems: 'center',
            backgroundColor: '#FEFCE8',
            border: '1px solid #e3e4df',
            borderRadius: '8px',
            padding: '12px 16px',
            marginRight: '8px',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
            transition: '0.3s ease-in-out',
            whiteSpace: 'nowrap',
            height: '52px',
            width: '272px'
        });

        // Create label
        const label = document.createElement('label');
        label.textContent = 'UID';
        label.htmlFor = 'mock-ndef-uid-input';
        Object.assign(label.style, {
            fontSize: '14px',
            fontFamily: 'system-ui, sans-serif',
            fontWeight: '600',
            color: '#282828',
            marginRight: '8px',
            flexShrink: '0'
        });

        // Create input field
        this.mockInput = document.createElement('input');
        this.mockInput.id = 'mock-ndef-uid-input';
        this.mockInput.type = 'text';
        this.mockInput.value = MockNDEFReader.mockTagUID;
        Object.assign(this.mockInput.style, {
            border: '1px solid #e3e4df',
            borderRadius: '4px',
            padding: '4px 8px',
            fontSize: '14px',
            fontFamily: 'system-ui, sans-serif',
            backgroundColor: '#ffffff',
            color: '#282828',
            outline: 'none',
            width: '100%'
        });

        inputBox.appendChild(label);
        inputBox.appendChild(this.mockInput);

        // Create button
        this.mockButton = document.createElement('button');
        this.mockButton.type = 'button';
        this.mockButton.textContent = '🛜 Simulate NFC Tag';
        Object.assign(this.mockButton.style, {
            position: 'absolute',
            right: '10px',
            top: '10px',
            flexGrow: '1',
            backgroundColor: '#FEFCE8',
            color: '#282828',
            border: '1px solid #e3e4df',
            borderRadius: '8px',
            padding: '12px 20px',
            fontSize: '16px',
            fontFamily: 'system-ui, sans-serif',
            fontWeight: '600',
            cursor: 'pointer',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
            transition: 'all 0.2s ease',
            height: '52px',
            display: 'block'
        });

        // Add hover effect to show input box
        this.mockButton.onmouseenter = () => {
            inputBox.style.right = '206px';
            inputBox.style.opacity = '1';
            if (this.mockButton) {
                this.mockButton.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.25)';
            }
        };

        this.mockContainer.onmouseleave = () => {
            inputBox.style.right = '-85px';
            inputBox.style.opacity = '0';
            if (this.mockButton) {
                this.mockButton.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.15)';
            }
        };

        // Add click handler to update UID and trigger 'reading' event
        this.mockButton.onclick = () => {
            if (this.mockInput) {
                MockNDEFReader.mockTagUID = this.mockInput.value;
            }
            this.triggerReadingEvent();
        };

        this.mockContainer.appendChild(inputBox);
        this.mockContainer.appendChild(this.mockButton);
        document.body.appendChild(this.mockContainer);
    }

    /**
     * Trigger a mock 'reading' event with the configured tag UID
     */
    private triggerReadingEvent(): void {
        const mockMessage: NDEFMessage = {
            records: []
        };

        const event = {
            message: mockMessage,
            serialNumber: MockNDEFReader.mockTagUID
        };

        // Notify all registered reading listeners
        this.readingListeners.forEach(listener => {
            listener(event);
        });
    }

    /**
     * Clean up and remove the mock button from the DOM
     */
    public cleanup(): void {
        if (this.mockContainer && this.mockContainer.parentNode) {
            this.mockContainer.parentNode.removeChild(this.mockContainer);
            this.mockContainer = null;
            this.mockButton = null;
            this.mockInput = null;
        }
    }
}

/**
 * Initialize the mock NDEFReader in development mode
 * This should be called once when the app initializes
 */
export function initMockNDEFReader(): void {
    if (typeof window !== 'undefined' && process.env.NODE_ENV === 'development') {
        // Replace the global NDEFReader with our mock implementation
        (window as unknown as { NDEFReader: typeof MockNDEFReader }).NDEFReader = MockNDEFReader;
        console.log('[MockNDEFReader] Mock NFC reader initialized for development');
    }
}

// Call the initialization function immediately
initMockNDEFReader();
